import { InventoryCount, InventoryOverviewSessionRow } from '../../../services/inventory/inventoryService';
import { addPeriod, getPeriodNumber, startOfPeriod } from '../../../utils/dateUtils';

/**
 * A group of section counts that together make up one inventory "session".
 * Mirrors the backend /sessions grouping: counts sharing a sessionId collapse
 * into one session; legacy rows (sessionId === null) collapse by calendar date.
 */
export interface SessionGroup {
  /** Stable key for the group (sessionId, or `legacy:<date>` for pre-session rows). */
  key: string;
  /** Most recent countedAt across the grouped counts. */
  completedAt: Date;
  sectionCount: number;
  totalValue: number;
  /** The inventory_group this session belonged to (a session is single-group). */
  groupId: string | null;
  groupName: string | null;
  /** The individual section counts in this session, newest first. */
  counts: InventoryCount[];
}

/** Local calendar date as `yyyy-mm-dd` — the "by day" bucket for pre-session rows. */
function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function legacyDateKey(date: Date): string {
  return `legacy:${localDateKey(date)}`;
}

/** Group flat per-section counts into sessions, newest session first. */
export function groupCountsIntoSessions(counts: InventoryCount[]): SessionGroup[] {
  const byKey = new Map<string, InventoryCount[]>();
  for (const c of counts) {
    const key = c.sessionId ? `session:${c.sessionId}` : legacyDateKey(c.countedAt);
    const list = byKey.get(key) ?? [];
    list.push(c);
    byKey.set(key, list);
  }

  const groups: SessionGroup[] = [];
  Array.from(byKey.entries()).forEach(([key, list]) => {
    const sorted = [...list].sort((a, b) => b.countedAt.getTime() - a.countedAt.getTime());
    // A session is single-group; surface the first count that carries a group label.
    const withGroup = sorted.find((c) => c.groupId) ?? sorted[0];
    groups.push({
      key,
      completedAt: sorted[0].countedAt,
      sectionCount: sorted.length,
      totalValue: sorted.reduce((sum, c) => sum + c.totalValue, 0),
      groupId: withGroup.groupId ?? null,
      groupName: withGroup.groupNameSnapshot ?? null,
      counts: sorted,
    });
  });

  return groups.sort((a, b) => b.completedAt.getTime() - a.completedAt.getTime());
}

/** Month bucket label key like "2026-06" for grouping session rows under headers. */
export function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Number of sessions whose completedAt falls in the current calendar month. */
export function countSessionsThisMonth(sessions: SessionGroup[], now: Date): number {
  const k = monthKey(now);
  return sessions.filter((s) => monthKey(s.completedAt) === k).length;
}

// ---------------------------------------------------------------------------
// All-restaurants overview (Inventory page, "All Restaurants" mode)
// ---------------------------------------------------------------------------

/** Food / Drinks / Other bucket for an inventory group. */
export type InventoryGroupType = 'food' | 'drinks' | 'other';

/**
 * Group names are FREE TEXT per restaurant (`inventory_groups.name`) — there is no type
 * column — so the Food/Drinks split is a name heuristic, computed client-side and never
 * persisted. Keep the keyword lists here, in one place, so a live-data mismatch is one edit.
 */
export const DRINKS_GROUP_KEYWORDS = ['drink', 'drank', 'dranken', 'bar', 'wijn', 'wine', 'beverage', 'bier', 'beer'];
export const FOOD_GROUP_KEYWORDS = ['food', 'eten', 'keuken', 'kitchen', 'voedsel'];

/**
 * Classify a group name. Drinks is tested FIRST on purpose: a group called "Bar & Food"
 * is rare, and when it happens drinks is the better guess than food.
 * Anything unmatched (or unnamed) lands in 'other' — never silently dropped.
 */
export function classifyInventoryGroup(name: string | null | undefined): InventoryGroupType {
  const n = (name ?? '').toLowerCase();
  if (!n) return 'other';
  if (DRINKS_GROUP_KEYWORDS.some((k) => n.includes(k))) return 'drinks';
  if (FOOD_GROUP_KEYWORDS.some((k) => n.includes(k))) return 'food';
  return 'other';
}

export interface OverviewKpis {
  total: number;
  food: number;
  drinks: number;
  other: number;
  /** How many groups fed each number — surfaces a "the names don't match" mismatch. */
  foodGroups: number;
  drinksGroups: number;
  otherGroups: number;
}

/**
 * Sum the "latest count per group" rows into the KPI row. Rows carry their own
 * `completedAt`, so this is a sum of latest counts — NOT a point-in-time valuation.
 */
export function buildOverviewKpis(rows: InventoryOverviewSessionRow[]): OverviewKpis {
  const kpis: OverviewKpis = { total: 0, food: 0, drinks: 0, other: 0, foodGroups: 0, drinksGroups: 0, otherGroups: 0 };
  for (const row of rows) {
    kpis.total += row.totalValue;
    const type = classifyInventoryGroup(row.groupName);
    if (type === 'food') {
      kpis.food += row.totalValue;
      kpis.foodGroups += 1;
    } else if (type === 'drinks') {
      kpis.drinks += row.totalValue;
      kpis.drinksGroups += 1;
    } else {
      kpis.other += row.totalValue;
      kpis.otherGroups += 1;
    }
  }
  return kpis;
}

export interface FeedWeekBuckets {
  thisWeek: InventoryOverviewSessionRow[];
  lastWeek: InventoryOverviewSessionRow[];
  earlier: InventoryOverviewSessionRow[];
}

/**
 * Split the loaded feed into this week / last week / earlier, Monday-based via the shared
 * `startOfPeriod(date, 'week')` — the same week boundaries the dashboard charts use.
 * Re-runs over the FULL loaded array after every "Load more", so a later page extending
 * the last-week bucket is correct by construction.
 */
export function bucketFeedByWeek(rows: InventoryOverviewSessionRow[], now: Date): FeedWeekBuckets {
  const thisWeekStart = startOfPeriod(now, 'week').getTime();
  const lastWeekStart = addPeriod(startOfPeriod(now, 'week'), 'week', -1).getTime();

  const buckets: FeedWeekBuckets = { thisWeek: [], lastWeek: [], earlier: [] };
  for (const row of rows) {
    const at = row.completedAt.getTime();
    if (at >= thisWeekStart) buckets.thisWeek.push(row);
    else if (at >= lastWeekStart) buckets.lastWeek.push(row);
    else buckets.earlier.push(row);
  }
  return buckets;
}

/** Monday-based week label ("W34") for the feed section headings. */
export function overviewWeekLabel(date: Date): string {
  return `W${getPeriodNumber(date, 'week')}`;
}

/**
 * Stable React key for an overview row. Legacy date-bucketed pseudo-sessions
 * (`sessionId === null`) MUST carry the restaurant id: the single-restaurant
 * `legacy:<date>` key would collide for two restaurants counting on the same day.
 */
export function overviewRowKey(row: InventoryOverviewSessionRow): string {
  return row.sessionId
    ? `session:${row.sessionId}`
    : `legacy:${row.restaurantId}:${localDateKey(row.completedAt)}`;
}
